/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useError from "../../hooks/useError";
import { LayoutContext } from "../../components/layout";
import Pagination from "../../components/Pagination";
import fetchHelper from "../../services/fetchHelper";
import AllReportsFilter from "./AllReportsFilter";
import UsersTable from "./UsersTable";

export const Loading = ({ length = 15, colspan = 12 }) => {
  const array = new Array(length);
  array.fill(1);
  return array.map((item, index) => (
    <tbody key={index}>
      <tr>
        <td colSpan={colspan}></td>
      </tr>
      <tr>
        <td colSpan={colspan} style={{ background: "#fff", padding: 0 }}>
          <img src="data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTUwNSA0NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KLnJlY3QgewpmaWxsOiB1cmwoI2xpbmVhci1ncmFkaWVudCk7Cn0KPC9zdHlsZT4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0ibGluZWFyLWdyYWRpZW50Ij4KPHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIzMyUiIHN0b3AtY29sb3I9IiNGN0Y3RjciIC8+CjxzdG9wIG9mZnNldD0iNTAlIiBzdG9wLWNvbG9yPSIjZWZmMGYwIiAvPgo8c3RvcCBvZmZzZXQ9IjY3JSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjRjdGN0Y3IiAvPgo8YW5pbWF0ZVRyYW5zZm9ybSBhdHRyaWJ1dGVOYW1lPSJncmFkaWVudFRyYW5zZm9ybSIKdHlwZT0idHJhbnNsYXRlIgpmcm9tPSItMSAwIgp0bz0iMSAwIgpiZWdpbj0iMHMiCmR1cj0iMS41cyIKcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgoKPHJlY3QgY2xhc3M9InJlY3QiIHg9IjAuOTgyOTEiIHk9IjAuMTY3NDgiIHdpZHRoPSIxNTAzLjE1IiBoZWlnaHQ9IjQ0LjgzMjUiIHJ4PSI2IiBmaWxsPSIjZWZmMGYwIi8+CjxyZWN0IGNsYXNzPSJyZWN0IiB4PSIwLjk4MjkxIiB5PSIwLjE2NzQ4IiB3aWR0aD0iMTUwMy4xNSIgaGVpZ2h0PSI0NC44MzI1IiByeD0iNiIgZmlsbD0idXJsKCNwYWludDBfbGluZWFyKSIvPgo8L3N2Zz4=" />
        </td>
      </tr>
    </tbody>
  ));
};

const AllReports = ({ activeTab, ...props }) => {
  const { user } = useContext(LayoutContext);
  const [filter, setFilter] = useState(null);
  const [defaultFilter, setDefaultFilter] = useState(null);
  const [textSearch, setTextSearch] = useState("");
  const [wildcard, setWildcard] = useState("");
  const [showAdvance, setShowAdvance] = useState(false);
  const [data, setData] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [offset, setOffset] = useState(0);
  const [to, setTo] = useState(null);
  const [limit, setLimit] = useState(30);
  const [total, setTotal] = useState(null);
  const [sort, setSort] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [initOffest, setInitOffset] = useState(true);
  const [url, setUrl] = useState("");
  const showDefaultApiError = useError();

  const { tab } = useParams();

  useEffect(() => {
    fetchPhysicians();
  }, []);

  useEffect(() => {
    if (user) {
      setFilter(null);
      setOffset(0);
      setWildcard("");
      setTextSearch("");
      setInitialLoad(true);
      setIsLoading(true);
      switch (tab) {
        default:
          setUrl(`/patientmanagement/rest/users`);
          setDefaultFilter({});
          setSort(["-createdAt"]);
          return;
      }
    }
  }, [tab, user]);

  const mapFilterKeys = {
    name: "fname",
    family: "lname",
    email: "email",
    "locations.name": "location",
    "readingPhysician.id": "assignedTo",
    thirtyDayBillStatus: "thirtyStatus",
    nintyDayBillStatus: "nintyStatus",
    interrogationType: "interrogationType",
  };

  const mapKeyFilters = {
    fname: { key: "name", com: "$lk" },
    lname: { key: "family", com: "$lk" },
    email: { key: "email", com: "$lk" },
    location: { key: "locations.name", com: "$eq" },
    assignedTo: { key: "readingPhysician.id", com: "$eq" },
    thirtyStatus: { key: "thirtyDayBillStatus", com: "$eq" },
    nintyStatus: { key: "nintyDayBillStatus", com: "$eq" },
    interrogationType: { key: "interrogationType", com: "$lk" },
  };

  const onSearchSubmittion = () => {
    let filters = {};
    if (textSearch) {
      const item = (i) => mapKeyFilters[i.split(":")[0]];
      textSearch.split(" ").forEach((i) => {
        if (i.split(":")[0] && i.split(":")[1])
          filters[item(i).key] = { key: item(i).key, com: item(i).com, value: i.split(":")[1] };
      });
      if (Object.keys(filters).length === 0) {
        const wildCard = textSearch
          .replace(/\s\s+/g, " ")
          .split(" ")
          .map((ts) => ({ name: { $lk: ts }, family: { $lk: ts } }));
        setTextSearch(textSearch.replace(/\s\s+/g, " "));
        setWildcard(wildCard);
      }
      setIsLoading(true);
    } else {
      setOffset(0);
      setWildcard("");
    }
    setFilter(filters);
    // fetchData();
  };

  useEffect(() => {
    if (defaultFilter) fetchData();
  }, [defaultFilter]);

  useEffect(() => {
    if (!initOffest) {
      setInitialLoad(true);
      fetchData();
    }
  }, [offset]);

  useEffect(() => {
    if (filter) {
      setIsLoading(true);
      setInitialLoad(true);
      const result = Object.keys(filter)
        .map((key) => `${mapFilterKeys[key]}:${filter[key].value}`)
        .join(" ");
      if (result) setTextSearch(result);
      fetchData();
      if (Object.keys(filter).length === 0) {
        if (textSearch.search(":") !== -1) setTextSearch("");
        else setTextSearch(textSearch);
      }
    } else setTextSearch(textSearch);
  }, [filter]);

  const fetchData = async () => {
    try {
      // if (textSearch && textSearch !== "") {
      //   where.push({ "patient.name": { $lk: textSearch } });
      //   where.push({ "patient.family": { $lk: textSearch } });
      // }
      const where = [];
      Object.keys(defaultFilter).map((f) => {
        const data = defaultFilter[f];
        where.push({ [data.key]: { [data.com]: data.value } });
      });
      if (wildcard) where.push(...wildcard);
      if (filter)
        Object.keys(filter).map((f) => {
          const data = filter[f];
          where.push({ [data.key]: { [data.com]: data.value } });
        });

      const urlParams = {
        where: JSON.stringify(where),
        match: "AND",
        matchInner: "OR",
        sort: sort.join(","),
      };
      const urlSearch = new URLSearchParams(urlParams);

      const { data, responseHeaders } = await fetchHelper.get({
        url: `${url}?${urlSearch}`,
        // urlDev: "/jsons/getNewInbox.json?" + urlSearch,
        headers: {
          "X-Range": `items=${offset}-${offset + limit - 1}`,
          Range: `items=${offset}-${offset + limit - 1}`,
        },
        includeResponseHeaders: ["Content-Range"],
      });
      setTo(offset + data.length - 1);
      setData(data);
      setTotal(+responseHeaders["Content-Range"].split("/")[1]);
      setInitOffset(false);
    } catch (err) {
      showDefaultApiError(err);
      setData([]);
      setTotal(null);
      setTo(null);
      setInitOffset(true);
      console.log(err);
    } finally {
      setInitialLoad(false);
      setIsLoading(false);
    }
  };

  const fetchPhysicians = async () => {
    try {
      const lS = window.localStorage.getItem("genToken");
      let url = "/patientmanagement/rest/users/readphysicians";
      const response = await fetch(url, {
        headers: {
          token: JSON.parse(lS)["token"],
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const json = await response.json();
        setPhysicians(json);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <div className="content">
        <div className="searchAdv" id="showSearch">
          <div className="search">
            <div className="row rowStretch searchIn framing">
              <div className="col col-s-100 col-m-80 col-l-80 col70 searchInCol">
                <label>
                  <input
                    type="text"
                    placeholder="Type any term(s) to begin search..."
                    value={textSearch}
                    onChange={(e) => setTextSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        onSearchSubmittion();
                      }
                    }}
                  />
                  <button
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                      padding: 15,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      onSearchSubmittion();
                      // setData([]);
                      // fetchData();
                    }}
                  >
                    {isLoading ? (
                      <div
                        id="results-loading-icon"
                        className="saving-white-ico icon-64"
                        style={{ height: 18, width: 18, filter: "invert()" }}
                      />
                    ) : (
                      <div
                        className="search-icon icon-64"
                        style={{ filter: "invert()", width: "15px", height: "15px" }}
                      ></div>
                    )}
                  </button>
                </label>
              </div>
              <div className="col col-s-100 col-m-20 col-l-10 col10 advSearchCol">
                <div
                  className="col col50 colBtnAdvSearch"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <button
                    className="btn btnTeal hideAdv"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-around",
                      maxWidth: "200px",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowAdvance(!showAdvance);
                    }}
                  >
                    <div id="advanced-search-btn" className="search-icon icon-64"></div>{" "}
                    {showAdvance ? "Hide" : "Show"} Advanced
                  </button>
                </div>
              </div>
            </div>
          </div>
          {showAdvance ? (
            <AllReportsFilter
              onFilterChange={(filter) => setFilter(filter)}
              filter={filter}
              activeTab={tab}
            />
          ) : null}
        </div>
      </div>

      <UsersTable
        data={data}
        initialLoad={initialLoad}
        user={user}
        fetchData={async () => {
          setInitialLoad(true);
          await fetchData();
        }}
      />

      <Pagination
        offset={offset}
        to={to}
        limit={limit}
        onSetOffset={setOffset}
        total={total}
        loading={initialLoad}
      />
    </>
  );
};

export default AllReports;
